import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Sparkles, Layers, ShieldCheck, ClipboardList, Coins, HelpCircle, 
  ArrowRight, Search, Zap, Plus, Trash2, Edit3, Eye, Sliders, Check, 
  AlertTriangle, PhoneCall, MessageCircle, FileText, CheckCircle, Database, Server
} from 'lucide-react';
import { NexusUser, WorkSuiteTask } from '../types/nexus';

export interface AIProduct {
  id: string;
  productName: string;
  category: string;
  shortDescription: string;
  problemSolved: string;
  targetCustomer: string;
  modulesUsed: string[];
  setupPrice: string;
  monthlyPrice: string;
  deliveryTime: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Expert';
  automationLevel: 'Manual Setup' | 'Assisted Automation' | 'Human Approved Automation' | 'Semi-Automated' | 'Fully Automated Internal Only';
  humanApprovalRequired: boolean;
  requiredIntegrations: string;
  clientDashboardIncluded: boolean;
  billingStatus: 'Price Placeholder' | 'Invoice Only' | 'Secure Checkout Ready' | 'Subscription Ready' | 'Not Live Yet';
  productStatus: 'Idea' | 'Draft' | 'Build Ready' | 'Client Preview Ready' | 'Live Service' | 'Paused' | 'Disabled';
  publicPageStatus: 'No Public Page' | 'Draft Landing Page' | 'Ready for Review' | 'Published';
  demoStatus: 'Active' | 'Under Construction' | 'Draft' | 'None';
  tags: string[];
  createdAt: string;
  updatedAt: string;
  // admin-only fields
  internalBuildNotes: string;
  riskNotes: string;
  supplierProviderNotes: string;
  costEstimate: string;
  marginEstimate: string;
  integrationNotes: string;
}

interface NexusAIProductFactoryProps {
  user: NexusUser;
  isAdminMode: boolean;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite: (task: Partial<WorkSuiteTask>) => void;
  currentRole: 'admin' | 'client' | 'anonymous' | 'god-architect';
}

export const CATEGORIES = [
  'AI Chat Products',
  'AI Search Products',
  'AI Agent Products',
  'AI Website Products',
  'AI WhatsApp Products',
  'AI Customer Support Products',
  'AI Research Products',
  'AI Social Media Products',
  'AI Video & Media Products',
  'AI Smart Gadget Products',
  'AI Business Dashboard Products',
  'AI Automation Products',
  'AI Local Business Products',
  'AI E-commerce Products',
  'AI Admin & Back Office Products'
];

export const MODULES_POOL = [
  'Nexus Chat',
  'Nexus Search',
  'Nexus Agent',
  'Nexus WorkSuite',
  'Nexus Hosting',
  'Nexus Mail & Domains',
  'Nexus Media Studio',
  'AI Ad Factory',
  'UGC Creator Studio',
  'Social Media Automation',
  'Nexus Smart Gadget Marketplace',
  'Nexus Future Gadget Discovery',
  'Nexus Tech Update Radar',
  'Product Scout Agent',
  'Billing Hub',
  'Client Dashboard',
  'Admin Control Hub'
];

const MODULE_MATRIX = [
  {
    module: 'Nexus Chat',
    adds: 'AI conversational interface, direct FAQ answering, and automated messaging workflows.',
    ideas: 'Customer Support Assistant, Website Chat Assistant, WhatsApp Lead Assistant'
  },
  {
    module: 'Nexus Search',
    adds: 'Web querying, live content fetching, and deep product/source directory searches.',
    ideas: 'Research Engine, Product Discovery Engine, Competitor Research Pack'
  },
  {
    module: 'Nexus Agent',
    adds: 'Autonomous task execution, prompt sequencing, and draft creation runs.',
    ideas: 'AI Automation Assistant, Lead Research Agent, Support Draft Agent'
  },
  {
    module: 'Nexus WorkSuite',
    adds: 'Integrated project task tracking, client-level tickets, and team-facing board approvals.',
    ideas: 'Client Dashboard, Operations Dashboard, Project Management AI'
  },
  {
    module: 'Nexus Hosting',
    adds: 'Instant web hosting deployment and live client preview previews.',
    ideas: 'AI Website Package, Landing Page Package, Business Website System'
  },
  {
    module: 'Nexus Mail & Domains',
    adds: 'Professional business domains and automatic mailbox creations for automated inbound routing.',
    ideas: 'Business Email Setup, Support Inbox, Sales Inbox Automation'
  },
  {
    module: 'Nexus Media Studio',
    adds: 'Video rendering capabilities, UGC scripts, and dynamic AI ad templates.',
    ideas: 'AI Content Pack, Promo Video Pack, Social Media Pack'
  },
  {
    module: 'Smart Gadget Marketplace',
    adds: 'Public display grids, affiliate and external checkout disclosures, and product listing tools.',
    ideas: 'Product Discovery Marketplace, Tech Watchlist, Supplier Pipeline'
  },
  {
    module: 'Tech Update Radar',
    adds: 'Tactical technology surveillance feeds and automated newsletter creation cards.',
    ideas: 'AI Update Watchlist, Client Advisory Reports, Automation Opportunity Alerts'
  }
];

export const DEFAULT_PRODUCTS: AIProduct[] = [
  {
    id: 'prod-web-assistant',
    productName: 'Nexus AI Website Assistant',
    category: 'AI Website Products',
    shortDescription: 'An AI-enhanced website package with chat, lead capture, service pages, contact forms, hosting, and client dashboard.',
    problemSolved: 'Helps local businesses establish automated touchpoints and professional sites instantly.',
    targetCustomer: 'Small businesses, trades, restaurants, agencies, consultants',
    modulesUsed: ['Nexus Hosting', 'Nexus Chat', 'Nexus WorkSuite', 'Nexus Mail & Domains', 'Billing Hub'],
    setupPrice: '£499',
    monthlyPrice: '£49/month',
    deliveryTime: '5 Days',
    difficulty: 'Medium',
    automationLevel: 'Assisted Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'Domain name DNS, Stripe Account',
    clientDashboardIncluded: true,
    billingStatus: 'Subscription Ready',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Published',
    demoStatus: 'Active',
    tags: ['SaaS', 'Web Builder', 'Chatbot', 'Local Business'],
    createdAt: '2026-06-18T01:00:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Uses solid Vite templating for the client preview. Host deploying via automated Docker script.',
    riskNotes: 'DNS propagation may cause transient support queries in first 24h.',
    supplierProviderNotes: 'Cloud hosting costs average £3/mo per sub-tenant.',
    costEstimate: '£15 setup, £4/month support line',
    marginEstimate: '92%',
    integrationNotes: 'API triggers to Hosting dispatch queue.'
  },
  {
    id: 'prod-customer-support',
    productName: 'Nexus AI Customer Support Assistant',
    category: 'AI Customer Support Products',
    shortDescription: 'AI assistant for handling website questions, support enquiries, FAQ replies, and customer service draft responses.',
    problemSolved: 'Reduces manual customer service hours by pre-drafting 90% of repeatable query resolutions.',
    targetCustomer: 'Small businesses, e-commerce, service companies',
    modulesUsed: ['Nexus Chat', 'Nexus Agent', 'Nexus WorkSuite', 'Nexus Mail & Domains'],
    setupPrice: '£299',
    monthlyPrice: '£39/month',
    deliveryTime: '3 Days',
    difficulty: 'Easy',
    automationLevel: 'Human Approved Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'SMTP credentials or support sub-domain',
    clientDashboardIncluded: true,
    billingStatus: 'Subscription Ready',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Draft Landing Page',
    demoStatus: 'Active',
    tags: ['Customer Support', 'Help Desk', 'Chat Agent', 'Email Assist'],
    createdAt: '2026-06-18T02:00:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Strictly holds responses as drafts in WorkSuite PM for review.',
    riskNotes: 'Do not connect live outbound SMTP directly without human approval step enabled.',
    supplierProviderNotes: 'LLM pricing from provider averages £0.05 per 100 queries.',
    costEstimate: '£2/month LLM credits',
    marginEstimate: '94%',
    integrationNotes: 'Relies on Nexus Chat session buffers.'
  },
  {
    id: 'prod-whatsapp-lead',
    productName: 'Nexus WhatsApp Lead Assistant',
    category: 'AI WhatsApp Products',
    shortDescription: 'Prepare WhatsApp business workflows for leads, bookings, support messages, and follow-up drafts.',
    problemSolved: 'Structures unstructured messaging into organized follow-up pipelines.',
    targetCustomer: 'Restaurants, clinics, garages, local services, online sellers',
    modulesUsed: ['Nexus Chat', 'Nexus Agent', 'Nexus WorkSuite'],
    setupPrice: '£399',
    monthlyPrice: '£59/month',
    deliveryTime: '7 Days',
    difficulty: 'Hard',
    automationLevel: 'Human Approved Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'Meta Business Suite, Verified WhatsApp number',
    clientDashboardIncluded: true,
    billingStatus: 'Invoice Only',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Draft Landing Page',
    demoStatus: 'Under Construction',
    tags: ['WhatsApp', 'Lead Capture', 'Instant Messaging', 'Workflows'],
    createdAt: '2026-06-18T03:00:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Requires manual setup with Meta Business Center. Ensure client has opt-in compliance checkboxes.',
    riskNotes: 'Adhere to strict Meta pricing guidelines. Do not bulk spam.',
    supplierProviderNotes: 'Meta API charges per conversation fee depending on region.',
    costEstimate: '£15 Meta fee buffer',
    marginEstimate: '75%',
    integrationNotes: 'Provides template approval drafting utility.'
  },
  {
    id: 'prod-research-report',
    productName: 'Nexus AI Research & Report Engine',
    category: 'AI Research Products',
    shortDescription: 'Search the web, collect information, summarise findings, generate reports, and send outputs to WorkSuite.',
    problemSolved: 'Automates long competitor research sweeps and market brief generations.',
    targetCustomer: 'Consultants, marketers, researchers, agencies, small businesses',
    modulesUsed: ['Nexus Search', 'Nexus Agent', 'Nexus WorkSuite'],
    setupPrice: '£299',
    monthlyPrice: 'Search credits plus support plan',
    deliveryTime: '2 Days',
    difficulty: 'Medium',
    automationLevel: 'Human Approved Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'Nexus Search API Key and storage bucket',
    clientDashboardIncluded: true,
    billingStatus: 'Subscription Ready',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Draft Landing Page',
    demoStatus: 'Active',
    tags: ['Research', 'Market Reports', 'Competitor Sweeper', 'PDF Export'],
    createdAt: '2026-06-18T04:00:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Web search runs on Google Serper and Gemini endpoints.',
    riskNotes: 'Ensure source accreditation in generated PDFs to respect copy laws.',
    supplierProviderNotes: 'Search API costing is £1 per 1,000 queries.',
    costEstimate: '£5/month query credits',
    marginEstimate: '85%',
    integrationNotes: 'Logs search queries to local database.'
  },
  {
    id: 'prod-social-media',
    productName: 'Nexus AI Social Media Engine',
    category: 'AI Social Media Products',
    shortDescription: 'Create content ideas, captions, campaign packs, video scripts, social schedules, and approval workflows.',
    problemSolved: 'Stops local business social media pages from looking inactive and dry.',
    targetCustomer: 'Small businesses, creators, agencies, local brands',
    modulesUsed: ['Nexus Media Studio', 'AI Ad Factory', 'UGC Creator Studio', 'Social Media Automation', 'Nexus WorkSuite'],
    setupPrice: '£349',
    monthlyPrice: '£49/month',
    deliveryTime: '4 Days',
    difficulty: 'Medium',
    automationLevel: 'Human Approved Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'Facebook Graph API or direct posting hook',
    clientDashboardIncluded: true,
    billingStatus: 'Subscription Ready',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Published',
    demoStatus: 'Active',
    tags: ['Social Content', 'Copywriting', 'Ad Studio', 'Creative Pack'],
    createdAt: '2026-06-18T04:30:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Builds beautiful graphic templates with canvas injection wrappers.',
    riskNotes: 'Strict warning: client must approve all social captions in WorkSuite dashboard before they queue.',
    supplierProviderNotes: 'Stable diffusion rendering and API fees flat £4 a month.',
    costEstimate: '£8/month media budget',
    marginEstimate: '83%',
    integrationNotes: 'Hooks into visual content synthesis pipeline.'
  },
  {
    id: 'prod-discovery-engine',
    productName: 'Nexus Product Discovery Engine',
    category: 'AI Smart Gadget Products',
    shortDescription: 'Discover smart gadgets, compare products, track future product launches, and build supplier watchlists.',
    problemSolved: 'Automates retail product scouting, sourcing matching, and store watches.',
    targetCustomer: 'Retailers, affiliates, gadget reviewers, e-commerce startups',
    modulesUsed: ['Nexus Smart Gadget Marketplace', 'Nexus Future Gadget Discovery', 'Product Scout Agent', 'Nexus Search'],
    setupPrice: '£399',
    monthlyPrice: '£49/month plus search credits',
    deliveryTime: '4 Days',
    difficulty: 'Hard',
    automationLevel: 'Human Approved Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'Product database connector, affiliate network tracking',
    clientDashboardIncluded: true,
    billingStatus: 'Subscription Ready',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Draft Landing Page',
    demoStatus: 'Active',
    tags: ['Smart Gadgets', 'Affiliate Sourcing', 'E-commerce Sourcing', 'Future Tech'],
    createdAt: '2026-06-18T04:45:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Connects directly to our Nexus active Scout Agent model parameters.',
    riskNotes: 'Wholesale inventory costs are external and remain client obligation.',
    supplierProviderNotes: 'Supplier search is powered by our text directory parser.',
    costEstimate: '£10/month server bandwidth',
    marginEstimate: '80%',
    integrationNotes: 'Integrates with Smart Gadget Marketplace active states.'
  },
  {
    id: 'prod-local-growth',
    productName: 'Nexus Local Business Growth Pack',
    category: 'AI Local Business Products',
    shortDescription: 'A complete AI toolkit for local businesses including website, chat, search, content, booking, reviews, and follow-up workflows.',
    problemSolved: 'Solves the fragmented toolkit problem by providing an all-in-one organic hub.',
    targetCustomer: 'Takeaways, salons, trades, driving schools, clinics, repair shops',
    modulesUsed: ['Nexus Hosting', 'Nexus Chat', 'Nexus WorkSuite', 'Social Media Automation', 'Nexus Mail & Domains', 'Billing Hub'],
    setupPrice: '£699',
    monthlyPrice: '£79/month',
    deliveryTime: '10 Days',
    difficulty: 'Expert',
    automationLevel: 'Human Approved Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'Google My Business, Domain config, Calendly or checkout links',
    clientDashboardIncluded: true,
    billingStatus: 'Secure Checkout Ready',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Published',
    demoStatus: 'Active',
    tags: ['All-in-One', 'Local Growth', 'Booking Engine', 'Review Booster'],
    createdAt: '2026-06-18T04:50:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Our highest-converting bundle. Integrates multi-page setup structures.',
    riskNotes: 'Verify local review compliance before deploying visual reviews card.',
    supplierProviderNotes: 'Host, email, and notification costs flat £12 per unit.',
    costEstimate: '£20/month host & communications fee',
    marginEstimate: '74%',
    integrationNotes: 'Triggers multi-module initializer script.'
  },
  {
    id: 'prod-business-dashboard',
    productName: 'Nexus AI Business Dashboard',
    category: 'AI Business Dashboard Products',
    shortDescription: 'A private dashboard for managing enquiries, tasks, content, search results, support tickets, products, and business workflows.',
    problemSolved: 'Consolidates multi-platform support, project management, and business ops into one portal.',
    targetCustomer: 'Small businesses, agencies, consultants, teams',
    modulesUsed: ['Client Dashboard', 'Nexus WorkSuite', 'Nexus Agent', 'Nexus Search', 'Billing Hub'],
    setupPrice: '£499',
    monthlyPrice: '£69/month',
    deliveryTime: '6 Days',
    difficulty: 'Expert',
    automationLevel: 'Human Approved Automation',
    humanApprovalRequired: true,
    requiredIntegrations: 'Company API hooks',
    clientDashboardIncluded: true,
    billingStatus: 'Subscription Ready',
    productStatus: 'Client Preview Ready',
    publicPageStatus: 'Published',
    demoStatus: 'Active',
    tags: ['Operations', 'CRM Admin', 'SaaS Panel', 'Task Board'],
    createdAt: '2026-06-18T04:55:00Z',
    updatedAt: '2026-06-18T05:00:00Z',
    internalBuildNotes: 'Uses local storage storage array and role-based permissions to safeguard sensitive notes.',
    riskNotes: 'Require strict user authentication to avoid cross-tenant information leaking.',
    supplierProviderNotes: 'Dedicated secure server setup averages £5/month.',
    costEstimate: '£15 billing and hosting resources',
    marginEstimate: '78%',
    integrationNotes: 'Binds with WorkSuite PM boards directly.'
  }
];

export const NexusAIProductFactory: React.FC<NexusAIProductFactoryProps> = ({
  user,
  isAdminMode,
  onAddLog,
  onAddTaskToWorkSuite,
  currentRole
}) => {
  // Theme state check (Uses the obsidian theme with electric glows)
  const [products, setProducts] = useState<AIProduct[]>(() => {
    const saved = localStorage.getItem('nexus_ai_products');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse AI products:", e);
      }
    }
    return DEFAULT_PRODUCTS;
  });

  useEffect(() => {
    localStorage.setItem('nexus_ai_products', JSON.stringify(products));
    window.dispatchEvent(new Event('nexus_ai_products_changed'));
  }, [products]);

  useEffect(() => {
    const handleProductsChange = () => {
      const saved = localStorage.getItem('nexus_ai_products');
      if (saved) {
        try {
          // Compare before setting state to avoid loops
          const parsed = JSON.parse(saved);
          if (JSON.stringify(parsed) !== JSON.stringify(products)) {
            setProducts(parsed);
          }
        } catch (e) {
          console.error("Failed to sync AI products:", e);
        }
      }
    };
    window.addEventListener('nexus_ai_products_changed', handleProductsChange);
    return () => {
      window.removeEventListener('nexus_ai_products_changed', handleProductsChange);
    };
  }, [products]);

  // View mode: 'admin_manager' or 'client_storefront'
  const [viewMode, setViewMode] = useState<'admin_manager' | 'client_storefront'>(
    isAdminMode ? 'admin_manager' : 'client_storefront'
  );

  useEffect(() => {
    // Sync initially with main dashboard mode
    setViewMode(isAdminMode ? 'admin_manager' : 'client_storefront');
  }, [isAdminMode]);

  // Selected filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  // Add/Edit Product form states
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [formProduct, setFormProduct] = useState<Partial<AIProduct>>({});

  // Sales landing page generation states
  const [viewingLandingPageProduct, setViewingLandingPageProduct] = useState<AIProduct | null>(null);
  const [landingPageHtml, setLandingPageHtml] = useState<any | null>(null);

  // Billing interactive simulation states
  const [billingSimResult, setBillingSimResult] = useState<string | null>(null);
  const [billingProduct, setBillingProduct] = useState<AIProduct | null>(null);

  // Console log entries for our dynamic API debugger
  const [apiLogs, setApiLogs] = useState<Array<{ id: string; timestamp: string; method: string; route: string; status: number; payload: any }>>([
    {
      id: 'log-init',
      timestamp: new Date().toLocaleTimeString(),
      method: 'GET',
      route: '/api/galaxy-lab/ai-products',
      status: 200,
      payload: { message: "Loaded default approved products.", total: DEFAULT_PRODUCTS.filter(p => p.productStatus === 'Client Preview Ready' || p.productStatus === 'Live Service').length }
    }
  ]);

  const addApiLog = (method: string, route: string, status: number, payload: any) => {
    const entry = {
      id: `apil--${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toLocaleTimeString(),
      method,
      route,
      status,
      payload
    };
    setApiLogs(prev => [entry, ...prev.slice(0, 15)]);
  };

  const handleCreateOrEditProduct = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formProduct.productName) return;

    if (editingProductId) {
      // Edit
      const updated = products.map(p => {
        if (p.id === editingProductId) {
          const finalProduct = {
            ...p,
            ...formProduct,
            updatedAt: new Date().toISOString()
          } as AIProduct;
          return finalProduct;
        }
        return p;
      });
      setProducts(updated);
      addApiLog('POST', `/api/admin/galaxy-lab/ai-products/${editingProductId}`, 200, { success: true, productId: editingProductId });
      onAddLog('Product Factory', `Modified product structure: ${formProduct.productName}`, 'SUCCESS', `Parameters saved strictly under permission gates.`);
    } else {
      // Create new
      const newId = `prod-${Math.random().toString(36).substr(2, 9)}`;
      const newProduct: AIProduct = {
        id: newId,
        productName: formProduct.productName || 'New AI Product',
        category: formProduct.category || 'AI Chat Products',
        shortDescription: formProduct.shortDescription || '',
        problemSolved: formProduct.problemSolved || '',
        targetCustomer: formProduct.targetCustomer || 'SMEs, Creators',
        modulesUsed: formProduct.modulesUsed || [],
        setupPrice: formProduct.setupPrice || 'From £299',
        monthlyPrice: formProduct.monthlyPrice || 'From £29/month',
        deliveryTime: formProduct.deliveryTime || '3 Days',
        difficulty: formProduct.difficulty || 'Medium',
        automationLevel: formProduct.automationLevel || 'Assisted Automation',
        humanApprovalRequired: formProduct.humanApprovalRequired !== undefined ? formProduct.humanApprovalRequired : true,
        requiredIntegrations: formProduct.requiredIntegrations || 'None',
        clientDashboardIncluded: formProduct.clientDashboardIncluded !== undefined ? formProduct.clientDashboardIncluded : true,
        billingStatus: formProduct.billingStatus || 'Price Placeholder',
        productStatus: formProduct.productStatus || 'Idea',
        publicPageStatus: formProduct.publicPageStatus || 'No Public Page',
        demoStatus: formProduct.demoStatus || 'None',
        tags: formProduct.tags || ['Custom'],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Admin-only fields
        internalBuildNotes: formProduct.internalBuildNotes || 'No internal build coordinates logged.',
        riskNotes: formProduct.riskNotes || 'No extreme risks reported.',
        supplierProviderNotes: formProduct.supplierProviderNotes || 'Standard cloud host resource usage only.',
        costEstimate: formProduct.costEstimate || '£0.00',
        marginEstimate: formProduct.marginEstimate || '90%',
        integrationNotes: formProduct.integrationNotes || 'Internal hook queue setup.'
      };

      setProducts(prev => [...prev, newProduct]);
      addApiLog('POST', '/api/admin/galaxy-lab/ai-products', 201, { success: true, createdId: newId });
      onAddLog('Product Factory', `Registered new AI Product: ${newProduct.productName}`, 'SUCCESS', `Stored in Scout array.`);
    }

    setIsEditorOpen(false);
    setFormProduct({});
    setEditingProductId(null);
  };

  const handleDeleteProduct = (id: string, name: string) => {
    if (confirm(`Are you sure you want to remove the product package: "${name}"?`)) {
      setProducts(prev => prev.filter(p => p.id !== id));
      addApiLog('DELETE', `/api/admin/galaxy-lab/ai-products/${id}`, 200, { success: true, removedId: id });
      onAddLog('Product Factory', `Archived product bundle: ${name}`, 'SUCCESS', `Removed from local persistence list.`);
    }
  };

  // Switcher to view Landing Page template
  const handleGenerateLandingPageDraft = (prod: AIProduct) => {
    addApiLog('POST', `/api/admin/galaxy-lab/ai-products/${prod.id}/create-landing-page`, 200, { status: "Draft generated", productId: prod.id });
    
    // Generate static metadata outlines for this draft
    const metaTitle = `Buy ${prod.productName} | Custom AI System`;
    const metaDescription = `Solve your business bottlenecks: ${prod.shortDescription}. Deploy premium AI integrations securely with full human-in-the-loop oversight.`;
    
    const draft = {
      heroHeadline: `Automate Your Growth Sourcing Loops with ${prod.productName}`,
      shortPitch: `Say goodbye to manual lag. We combine ${prod.modulesUsed.join(', ')} into a single, cohesive, client-ready automation engine tuned perfectly to your specific requirements.`,
      problem: `Managing complex modern tech portfolios causes excessive team cognitive load. Clients struggle with fractured logins, insecure API key handling, and inaccurate robotic automation.`,
      solution: `The consolidated ${prod.productName} replaces manual friction layers with human-guided, highly efficient AI modules acting under direct safety control parameters.`,
      features: [
        `Consolidated Client Control Deck for tracking status updates`,
        `Includes dynamic modules: ${prod.modulesUsed.slice(0, 3).join(', ')}`,
        `Pre-drafted messaging queues: fully editable before dispatch`,
        `Automated system health checks running silently in the background`
      ],
      benefits: [
        `Save up to 35 hours per week of manual clerical tasks`,
        `Protected by role permissions: zero confidential credential exposures`,
        `Fully backed by expert systems configurations and direct reviews`
      ],
      faq: [
        { q: "Is this fully autonomous?", a: "No. Safety is our paramount protocol. Outbound communications, WhatsApp schedules, and direct billing triggers require human approval steps before deployment." },
        { q: "How long does setup take?", a: `On average, our systems architects configure and provision your dedicated build within ${prod.deliveryTime}.` },
        { q: "Are custom modules available?", a: "Absolutely. We construct customized modules tailored exactly to your unique workflow requirements." }
      ],
      metaTitle,
      metaDescription
    };

    setLandingPageHtml(draft);
    setViewingLandingPageProduct(prod);
    onAddLog('Landing Page Generator', `Created landing page draft: ${prod.productName}`, 'SUCCESS', `Meta tags compiled & marked as Draft Outline.`);
  };

  // Simulating Billing Hub connection
  const handleSimulateBillingAction = (prod: AIProduct, optType: string) => {
    setBillingProduct(prod);
    let resultText = '';

    if (optType === 'invoice') {
      resultText = `📝 DRAFT INVOICE CREATED (QUOTE-ONLY)\n\nProduct: ${prod.productName}\nSetup Charge: ${prod.setupPrice}\nMaintenance Fee: ${prod.monthlyPrice}\n\nClient Account: ${user.email}\nStatus: Quote Required\n\n*Safe Billing Directive: Invoice holds draft state until secure payment provider handshake is established. Zero automated debits was enforced.`;
      addApiLog('POST', `/api/galaxy-lab/ai-products/${prod.id}/request-setup`, 200, { method: 'Request Invoice', productId: prod.id, setupPrice: prod.setupPrice });
    } else if (optType === 'checkout') {
      resultText = `💳 SECURE CHECKOUT AVAILABLE SOON\n\nRedirect token: PLACEHOLDER_PENDING_GATEWAY\nAmount: ${prod.setupPrice}\nRecurrence: ${prod.monthlyPrice}\n\n*System Wording Guard: Live checkouts are deactivated until direct integration configurations are securely linked. Ask support about direct account transfer options.`;
      addApiLog('POST', `/api/admin/galaxy-lab/ai-products/${prod.id}/add-to-pricing`, 200, { method: 'Secure Checkout Ready Preview', productId: prod.id });
    } else if (optType === 'subscription') {
      resultText = `🔄 SUBSCRIPTION REGISTRY INITIALIZED\n\nProfile Title: ${prod.productName} SaaS Plan\nMonthly Rate: ${prod.monthlyPrice}\nCancellation Period: 30-day notice\n\n*Operational check: Marked as subscription draft. Public clients can review estimates inside their secure dashboards.`;
    }

    setBillingSimResult(resultText);
    onAddLog('Billing Hub Gateway', `Simulated checkout event for ${prod.productName}`, 'SUCCESS', `${optType} configuration parsed safely.`);
  };

  // Dispatch worksuite build task
  const handleCreateWorkSuiteTask = (prod: AIProduct) => {
    const newTask: Partial<WorkSuiteTask> = {
      title: `Build & configure AI Product System: ${prod.productName}`,
      project: 'AI Product Factory Integration',
      client: user.email,
      priority: 'HIGH',
      status: 'TODO',
      assignedAgent: 'Product Scout Agent'
    };
    onAddTaskToWorkSuite(newTask);
    addApiLog('POST', `/api/admin/galaxy-lab/ai-products/${prod.id}/create-work-task`, 200, { success: true, taskTitle: newTask.title });
    onAddLog('WorkSuite PM Sync', `Launched build task for ${prod.productName}`, 'SUCCESS', 'Assigned dynamically to agent.');
    alert(`Task added successfully to WorkSuite Project Manager under client: ${user.email}!`);
  };

  // Dispatch to Agent Scout
  const handleSendToScoutAgent = (prod: AIProduct) => {
    addApiLog('POST', `/api/admin/galaxy-lab/ai-products/${prod.id}/send-to-agent`, 200, { success: true, dispatchedTo: 'Agent 09.2' });
    onAddLog('Agent Handler Queue', `Transmitted product brief ${prod.productName} to Product Scout Agent`, 'SUCCESS', 'Agent queued for competitor analysis report.');
    alert(`Brief sent! Product Scout Agent is analyzing wholesale supply lines and pricing guidelines for "${prod.productName}". Check terminal logs for updates.`);
  };

  // Matching logical filters
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.productName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.targetCustomer.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'ALL' || p.category === selectedCategory;

    // If client preview mode, only show Client Preview Ready or Live Service status, and hide Draft/Idea
    if (viewMode === 'client_storefront') {
      return matchesSearch && matchesCategory && (p.productStatus === 'Client Preview Ready' || p.productStatus === 'Live Service');
    }

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8 text-left">
      
      {/* HEADER SECTION IN OBSIDIAN & DUAL RADIAL ELECTRIC GLOWS */}
      <div className="relative p-6 md:p-8 rounded-3xl bg-zinc-950/80 border border-purple-500/25 overflow-hidden shadow-[0_0_25px_rgba(232,64,243,0.08)]">
        <div className="absolute top-0 right-0 w-48 h-48 bg-[#00ffff]/5 rounded-full blur-[60px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-12 w-48 h-48 bg-[#e840f3]/5 rounded-full blur-[60px] pointer-events-none"></div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#e840f3]/15 text-pink-400 border border-[#e840f3]/35 text-[9px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full">
                Ecosystem Business Layer
              </span>
              <span className="bg-[#00ffff]/15 text-[#00ffff] border border-[#00ffff]/35 text-[9px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full block md:inline">
                SaaS Dispatch Core
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-sans font-black tracking-tight text-white uppercase">
              Nexus AI Product Factory
            </h2>
            <p className="text-[11px] text-slate-400 max-w-2xl font-mono uppercase tracking-wide mt-1">
              Turn Nexus tools into sellable AI products, automations, dashboards, and client-ready service packages.
            </p>
          </div>

          {/* VIEW SWITCHER CONTROL PANEL */}
          <div className="flex items-center bg-black border border-zinc-800 p-1 rounded-xl shrink-0">
            <button
              onClick={() => {
                setViewMode('admin_manager');
                onAddLog('Product Factory View', 'Switched context to Product Manager Mode', 'SUCCESS', 'Displaying full administrative grid.');
              }}
              className={`px-3 py-2 text-[10px] font-mono uppercase font-black rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                viewMode === 'admin_manager'
                  ? 'bg-purple-950/40 text-purple-400 border border-purple-500/30'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Sliders className="w-3.5 h-3.5" />
              🛠️ Product Manager Mode
            </button>
            <button
              onClick={() => {
                setViewMode('client_storefront');
                onAddLog('Product Factory View', 'Switched context to Client Storefront Preview', 'SUCCESS', 'Masking sensitive supplier details.');
              }}
              className={`px-3 py-2 text-[10px] font-mono uppercase font-black rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                viewMode === 'client_storefront'
                  ? 'bg-cyan-950/40 text-[#00ffff] border border-[#00ffff]/30'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              👁️ Client Storefront Preview
            </button>
          </div>
        </div>

        <p className="text-slate-350 text-[11.5px] mt-4 font-sans leading-relaxed border-t border-zinc-900/80 pt-3 max-w-4xl">
          Nexus AI Product Factory helps NexoraX combine chat, search, agents, media, websites, hosting, email, social automation, product discovery, and business dashboards into premium, secure AI products that can be sold to clients with full compliance safeguarding.
        </p>
      </div>

      {/* SEARCH, CATEGORY FILTER & ADD BUTTON */}
      <div className="flex flex-col md:flex-row gap-3 justify-between items-stretch">
        <div className="flex flex-col sm:flex-row gap-2 flex-grow">
          {/* SEARCH FIELD */}
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search products, target clients, tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-zinc-950/80 border border-zinc-800 text-xs text-white rounded-xl pl-9 pr-4 py-2.5 focus:outline-none focus:border-[#00ffff] font-sans"
            />
          </div>

          {/* CATEGORY SELECT */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-zinc-950/80 border border-zinc-800 text-xs text-slate-300 rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#e840f3] uppercase font-mono tracking-tight"
          >
            <option value="ALL">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* CREATE PRODUCT BUTTON (ADMIN ONLY) */}
        {viewMode === 'admin_manager' && (
          <button
            onClick={() => {
              setEditingProductId(null);
              setFormProduct({
                productName: '',
                category: 'AI Chat Products',
                shortDescription: '',
                problemSolved: '',
                targetCustomer: '',
                modulesUsed: [],
                setupPrice: 'From £299',
                monthlyPrice: 'From £39/month',
                deliveryTime: '3 Days',
                difficulty: 'Medium',
                automationLevel: 'Assisted Automation',
                humanApprovalRequired: true,
                requiredIntegrations: 'None',
                clientDashboardIncluded: true,
                billingStatus: 'Price Placeholder',
                productStatus: 'Idea',
                publicPageStatus: 'No Public Page',
                demoStatus: 'None',
                tags: [],
                internalBuildNotes: '',
                riskNotes: '',
                supplierProviderNotes: '',
                costEstimate: '£10',
                marginEstimate: '90%',
                integrationNotes: ''
              });
              setIsEditorOpen(true);
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-mono text-[10.5px] font-black uppercase tracking-wider rounded-xl cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 shrink-0 shadow-[0_0_15px_rgba(168,85,247,0.25)] border border-pink-500/25"
          >
            <Plus className="w-4 h-4" />
            Create AI Product Draft
          </button>
        )}
      </div>

      {/* CORE DISPLAY GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* MAIN PRODUCT CATALOG - 8 COLS */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-mono text-xs uppercase tracking-wider text-slate-400 font-extrabold flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00ffff] animate-pulse"></span>
              {viewMode === 'admin_manager' ? 'Admin Master Packages Index' : 'Available Client AI Products'} ({filteredProducts.length})
            </h3>
            <span className="text-[10px] font-mono text-slate-500 uppercase">
              {viewMode === 'admin_manager' ? 'Full View Enabled' : 'Sensitive supplier fields hidden'}
            </span>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="p-12 text-center bg-zinc-950/60 border border-zinc-900 rounded-3xl space-y-3">
              <AlertTriangle className="w-8 h-8 text-slate-500 mx-auto" />
              <h4 className="text-xs font-mono uppercase text-slate-400">No packaged matches found</h4>
              <p className="text-[11px] text-slate-500 font-sans">Modify your filter constraints or reset keywords to view standard solutions.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence mode="popLayout">
                {filteredProducts.map(prod => {
                  const isDemoActive = prod.demoStatus === 'Active';
                  return (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      key={prod.id}
                      className="p-5 rounded-2xl bg-gradient-to-b from-zinc-950/90 to-black border border-purple-500/20 hover:border-cyan-500/40 relative overflow-hidden transition-all group flex flex-col justify-between shadow-[0_4px_20px_rgba(0,0,0,0.4)] hover:shadow-[0_0_20px_rgba(0,255,255,0.06)]"
                    >
                      {/* Purple Glass Accent and Neon Cyan Border Highlight */}
                      <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></div>
                      
                      {/* Product Headline Information */}
                      <div className="space-y-3">
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <span className="bg-zinc-900 text-slate-400 border border-zinc-800 text-[8px] font-mono uppercase tracking-wider px-2 py-0.5 rounded select-none">
                              {prod.category}
                            </span>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {prod.humanApprovalRequired && (
                              <span className="bg-amber-955 text-amber-400 border border-amber-500/30 text-[7.5px] font-mono tracking-tight uppercase px-1.5 py-0.5 rounded flex items-center gap-1 select-none">
                                <ShieldCheck className="w-2.5 h-2.5" /> Require Manual Review
                              </span>
                            )}
                            <span className={`text-[8px] font-mono uppercase px-1.5 py-0.5 rounded tracking-wide ${
                              prod.productStatus === 'Live Service' ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-500/20' :
                              prod.productStatus === 'Client Preview Ready' ? 'bg-cyan-950/40 text-cyan-400 border border-cyan-500/20' :
                              'bg-zinc-900 text-zinc-500 border border-zinc-800'
                            }`}>
                              {prod.productStatus}
                            </span>
                          </div>
                        </div>

                        <h4 className="text-sm font-sans font-black text-white group-hover:text-[#00ffff] transition-colors leading-tight">
                          {prod.productName}
                        </h4>
                        
                        <p className="text-[11.5px] text-slate-400 leading-relaxed font-sans font-medium line-clamp-3">
                          {prod.shortDescription}
                        </p>

                        {/* Modules Used Checklist Tags */}
                        <div className="space-y-1.5 pt-1">
                          <label className="text-[8px] font-mono text-slate-500 uppercase block tracking-wider font-extrabold">ECOSYSTEM MODULES INTEGRATED:</label>
                          <div className="flex flex-wrap gap-1">
                            {prod.modulesUsed.map((mod, i) => (
                              <span key={i} className="bg-purple-950/20 hover:bg-purple-900/35 border border-purple-500/20 text-[8.5px] font-mono text-purple-300 px-1.5 py-0.5 rounded transition-all">
                                {mod}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Problem solved */}
                        <div className="text-[10px] font-sans text-slate-450 border-l border-zinc-805 pl-2 py-0.5">
                          <span className="font-mono text-[8px] text-[#00ffff] uppercase block tracking-wider font-extrabold">PROBLEM SOLVED:</span>
                          {prod.problemSolved}
                        </div>

                        {/* Target Customer */}
                        <div className="text-[10px] text-slate-500 font-sans">
                          Target client: <strong className="text-slate-350">{prod.targetCustomer}</strong>
                        </div>
                      </div>

                      {/* Pricing block, metadata and CTA buttons */}
                      <div className="mt-5 space-y-4 pt-4 border-t border-zinc-900/80">
                        
                        {/* Display Pricing Placeholder - Safely stated as planning estimates */}
                        <div className="flex items-center justify-between bg-zinc-950/60 p-2 rounded-xl border border-zinc-900">
                          <div>
                            <span className="text-[8px] font-mono text-slate-500 block uppercase font-bold">SETUP ESTIMATE</span>
                            <span className="text-white text-xs font-black font-mono tracking-tight">{prod.setupPrice}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[8px] font-mono text-slate-500 block uppercase font-bold">MONTHLY FEE</span>
                            <span className="text-[#00ffff] text-xs font-black font-mono tracking-tight">{prod.monthlyPrice}</span>
                          </div>
                        </div>

                        {/* Admin-only detailed financial specifications */}
                        {viewMode === 'admin_manager' && (
                          <div className="p-3 bg-purple-950/15 border border-purple-500/20 rounded-xl text-[9.5px] font-mono space-y-1 text-slate-400">
                            <div className="text-purple-300 font-extrabold text-[8.5px] uppercase flex justify-between items-center pb-1 border-b border-purple-950">
                              <span>🔐 ADMIN COGNITIVE OVERWATCH</span>
                              <span className="text-[#00ffff]">{prod.marginEstimate} MARGIN</span>
                            </div>
                            <div>Internal Cost: <strong className="text-white">{prod.costEstimate}</strong></div>
                            <div>Risk Nodes: <span className="text-amber-400">{prod.riskNotes || 'None'}</span></div>
                            <div className="line-clamp-2">Supplier: <span className="text-slate-300">{prod.supplierProviderNotes}</span></div>
                            <div className="line-clamp-1 italic text-slate-500">Notes: {prod.internalBuildNotes}</div>
                          </div>
                        )}

                        {/* CTA ACTION SYSTEM BUTTONS BUTTONS AREA */}
                        <div className="flex flex-col gap-1.5">
                          
                          {/* Client facing actions */}
                          {viewMode === 'client_storefront' ? (
                            <div className="grid grid-cols-2 gap-1.5">
                              <button
                                onClick={() => handleSimulateBillingAction(prod, 'invoice')}
                                className="py-2 px-3 bg-cyan-900/30 hover:bg-cyan-900/50 border border-cyan-500/25 rounded-lg text-[9.5px] font-mono font-black uppercase text-cyan-300 cursor-pointer transition-all hover:scale-[1.01]"
                              >
                                Request Setup
                              </button>
                              <button
                                onClick={() => {
                                  alert(`Consultation booked! A systems integration architect will review your request for the "${prod.productName}" portfolio and schedule a briefing. Checked in logs.`);
                                  onAddLog('Client Interface', `Booked consultation briefing for: ${prod.productName}`, 'SUCCESS', `Dispatched inquiry packet.`);
                                }}
                                className="py-2 px-3 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 rounded-lg text-[9.5px] font-mono font-black uppercase text-slate-200 cursor-pointer transition-all"
                              >
                                Book consultation
                              </button>
                              <button
                                onClick={() => {
                                  alert(`Opening helper socket... You can ask our central AI models about configuring the "${prod.productName}".`);
                                  onAddLog('Client Interface', `Initiated custom chat prompt inquiry for: ${prod.productName}`, 'SUCCESS', `Triggered virtual room session.`);
                                }}
                                className="py-2 px-3 bg-black hover:bg-zinc-900 border border-zinc-805 rounded-lg text-[9.5px] font-mono uppercase text-slate-400 cursor-pointer transition-all col-span-2 text-center"
                              >
                                Ask Nexus Chat
                              </button>
                            </div>
                          ) : (
                            /* Admin facing actions */
                            <div className="space-y-1.5 text-xs font-mono">
                              <div className="grid grid-cols-3 gap-1">
                                <button
                                  onClick={() => {
                                    setEditingProductId(prod.id);
                                    setFormProduct(prod);
                                    setIsEditorOpen(true);
                                    onAddLog('Product Manager', `Opened editor wrapper for: ${prod.productName}`, 'SUCCESS', `Retrieved state structures.`);
                                  }}
                                  className="p-1.5 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 text-slate-300 text-[8.5px] font-black uppercase rounded flex items-center justify-center gap-1 cursor-pointer"
                                  title="Edit Product Details"
                                >
                                  <Edit3 className="w-3 h-3 text-[#00ffff]" /> Edit
                                </button>
                                <button
                                  onClick={() => handleGenerateLandingPageDraft(prod)}
                                  className="p-1.5 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 text-slate-300 text-[8.5px] font-black uppercase rounded flex items-center justify-center gap-1 cursor-pointer"
                                  title="Build Landing Page"
                                >
                                  <FileText className="w-3 h-3 text-[#e840f3]" /> landing
                                </button>
                                <button
                                  onClick={() => handleCreateWorkSuiteTask(prod)}
                                  className="p-1.5 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 text-slate-200 text-[8.5px] font-black uppercase rounded flex items-center justify-center gap-1 cursor-pointer"
                                  title="Create WorkSuite Task"
                                >
                                  <ClipboardList className="w-3 h-3 text-amber-500" /> PM Build
                                </button>
                              </div>

                              <div className="grid grid-cols-3 gap-1">
                                <button
                                  onClick={() => handleSendToScoutAgent(prod)}
                                  className="p-1.5 bg-purple-950/20 hover:bg-purple-900/30 border border-purple-500/20 text-purple-300 text-[8px] uppercase font-black rounded cursor-pointer"
                                  title="Dispatch Agent Scout"
                                >
                                  Scout Agent
                                </button>
                                <button
                                  onClick={() => handleSimulateBillingAction(prod, 'checkout')}
                                  className="p-1.5 bg-black hover:bg-zinc-900 border border-zinc-805 text-slate-400 text-[8px] uppercase rounded cursor-pointer"
                                  title="Add to Pricing Link"
                                >
                                  Pricing Sim
                                </button>
                                <button
                                  onClick={() => handleDeleteProduct(prod.id, prod.productName)}
                                  className="p-1.5 bg-rose-950/20 hover:bg-rose-900/30 border border-rose-500/25 text-rose-400 text-[8.5px] font-black uppercase rounded flex items-center justify-center gap-1 cursor-pointer"
                                  title="Archive/Delete Package"
                                >
                                  <Trash2 className="w-3 h-3" /> Delete
                                </button>
                              </div>
                            </div>
                          )}

                        </div>

                        {/* Safe wording disclaimer on guarantees */}
                        <div className="text-[8px] text-slate-500 font-mono tracking-normal leading-normal italic select-none">
                          * Pricing shown as planning estimate. Final quote depends on client requirements. No absolute income guarantees implied.
                        </div>

                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* SIDE BAR LAYOUT FOR MODULE MATRIX & INTERACTIVE DEBBUGGER CONSOLE - 4 COLS */}
        <div className="lg:col-span-4 space-y-6">
          
          {/* MODULE COMBINATION MATRIX COMPONENT */}
          <div className="p-5 rounded-2xl bg-zinc-950/90 border border-purple-500/20 shadow-md">
            <h4 className="font-mono text-[10px] uppercase font-extrabold text-white tracking-widest mb-3 flex items-center gap-2">
              <Layers className="w-3.5 h-3.5 text-purple-400" />
              Module Combination Matrix
            </h4>
            <p className="text-[10px] text-slate-400 font-sans mb-4 leading-normal">
              Review how our underlying software modules pair together to formulate high-value sellable services:
            </p>

            <div className="space-y-3.5 max-h-[380px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-zinc-800">
              {MODULE_MATRIX.map((item, idx) => (
                <div key={idx} className="p-3 bg-black/60 border border-zinc-900 rounded-xl hover:border-zinc-800 transition-colors space-y-1 text-left">
                  <div className="flex items-center gap-1 text-[10px] font-mono font-bold text-[#00ffff]">
                    <span className="w-1 h-3 bg-[#e840f3] rounded-full inline-block"></span>
                    {item.module}
                  </div>
                  <div className="text-[10px] text-slate-400 leading-normal font-sans">
                    <strong>Value:</strong> {item.adds}
                  </div>
                  <div className="text-[8.5px] font-mono text-[#e840f3] uppercase tracking-wide">
                    Suggested: {item.ideas}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* DYNAMIC API ROUTE RUNNER DEBUGGER (REQUIRED) */}
          <div className="p-5 rounded-2xl bg-black border border-zinc-850 shadow-md flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-mono text-[10px] uppercase font-extrabold text-[#00ffff] tracking-widest flex items-center gap-2">
                  <Database className="w-3.5 h-3.5" />
                  Backend API Router Logger
                </h4>
                <span className="animate-pulse bg-emerald-950 text-emerald-400 text-[7px] font-mono px-1.5 rounded uppercase">Connected</span>
              </div>
              <p className="text-[9.5px] text-slate-500 font-mono mb-3 uppercase tracking-wide">
                Simulating routing endpoints on secure port 3000:
              </p>

              <div className="space-y-1.5 max-h-[220px] overflow-y-auto font-mono text-[9px] pr-1 scrollbar-thin scrollbar-thumb-zinc-800">
                {apiLogs.map(log => (
                  <div key={log.id} className="p-2 bg-zinc-950 rounded border border-zinc-900 text-left space-y-1 hover:border-zinc-800 transition-colors">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">{log.timestamp}</span>
                      <span className={`px-1 rounded text-[8px] font-extrabold uppercase ${
                        log.status < 300 ? 'bg-emerald-950/60 text-emerald-400' : 'bg-rose-950 text-rose-450'
                      }`}>
                        STATUS: {log.status}
                      </span>
                    </div>
                    <div className="flex gap-2 items-center text-slate-205">
                      <strong className="text-purple-400">{log.method}</strong>
                      <span className="text-slate-300 font-bold">{log.route}</span>
                    </div>
                    <pre className="text-[8px] text-slate-500 overflow-x-auto whitespace-pre-wrap select-all bg-black/40 p-1 rounded max-h-[80px]">
                      {JSON.stringify(log.payload, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3.5 pt-3 border-t border-zinc-900 flex justify-between items-center text-[9px] text-[#00ffff] font-mono uppercase">
              <span>Secure Server-side Storage verified</span>
              <span>API Keys Protected</span>
            </div>
          </div>

        </div>

      </div>

      {/* ⚠️ SYSTEM SECURITY SAFEGUARDS INFORMATION FOOTER */}
      <div className="p-5 rounded-2xl bg-amber-950/15 border border-amber-500/20 flex flex-col md:flex-row gap-4 items-start text-left">
        <ShieldCheck className="w-8 h-8 text-amber-500 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h5 className="font-mono text-[11px] font-extrabold uppercase text-amber-400 tracking-wider">
            System Operational Wording Safety &amp; Human Approval Safeguards
          </h5>
          <p className="text-[11.5px] text-slate-350 leading-relaxed font-sans font-medium">
            <strong>Human Approval Required Gate:</strong> AI workflows under the Nexus brand can draft, organize, prepare, and summarize communications, but strictly <strong>CANNOT</strong> auto-publish, send external emails, dispatch WhatsApp, trigger billing, or contact suppliers without direct administrative review. Private API keys and sensitive customer financial details are strictly stored server-side. Secure checkout activities are hosted under invoice-only planning states.
          </p>
        </div>
      </div>

      {/* MODAL WINDOW : ADMIN PRODUCT BUILDER & EDITOR */}
      {isEditorOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-3xl bg-zinc-950 border border-[#e840f3]/40 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(232,64,243,0.15)] max-h-[90vh] flex flex-col text-left"
          >
            <div className="p-6 bg-black border-b border-zinc-900 flex justify-between items-center shrink-0">
              <h3 className="font-sans font-black text-white uppercase text-md tracking-tight">
                {editingProductId ? '🛠️ Edit Sourced AI Product Package' : '🏭 Model Custom AI Product Package'}
              </h3>
              <button
                onClick={() => setIsEditorOpen(false)}
                className="text-slate-400 hover:text-white font-mono text-xs cursor-pointer uppercase font-black"
              >
                [ CLOSE ]
              </button>
            </div>

            <form onSubmit={handleCreateOrEditProduct} className="p-6 overflow-y-auto space-y-6 text-xs text-slate-300">
              
              {/* PRIMARY PUBLIC SPECIFICATIONS */}
              <div className="space-y-4">
                <h4 className="font-mono text-[10px] uppercase font-bold text-cyan-400 border-b border-zinc-900 pb-1.5 tracking-widest">
                  Public Facing Package Coordinates
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Product Name</label>
                    <input
                      type="text"
                      required
                      value={formProduct.productName || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, productName: e.target.value }))}
                      placeholder="e.g. Nexus AI Website Assistant"
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white text-xs font-sans placeholder-slate-600 focus:outline-none focus:border-[#00ffff]"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Product Category</label>
                    <select
                      value={formProduct.category || 'AI Chat Products'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Short Description</label>
                    <textarea
                      rows={2}
                      value={formProduct.shortDescription || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, shortDescription: e.target.value }))}
                      placeholder="High fidelity description of services included..."
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white placeholder-slate-600 font-sans focus:outline-none focus:border-[#e840f3]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Problem Solved</label>
                    <input
                      type="text"
                      value={formProduct.problemSolved || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, problemSolved: e.target.value }))}
                      placeholder="e.g. Stops manual checkout delays"
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-sans"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Target Customer</label>
                    <input
                      type="text"
                      value={formProduct.targetCustomer || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, targetCustomer: e.target.value }))}
                      placeholder="e.g. Small takeaway restaurants, agencies"
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-sans"
                    />
                  </div>
                </div>

                {/* MODULES SELECTION GATES CHECKBOXES */}
                <div>
                  <label className="text-zinc-500 uppercase block mb-1.5 font-bold">Ecosystem Modules integrated</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-black border border-zinc-900 rounded-xl max-h-[140px] overflow-y-auto">
                    {MODULES_POOL.map((mod) => {
                      const modulesUsed = formProduct.modulesUsed || [];
                      const isChecked = modulesUsed.includes(mod);
                      return (
                        <label key={mod} className="flex items-center gap-1.5 text-[10px] text-slate-350 cursor-pointer hover:text-white select-none">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const nextMods = isChecked
                                ? modulesUsed.filter(m => m !== mod)
                                : [...modulesUsed, mod];
                              setFormProduct(prev => ({ ...prev, modulesUsed: nextMods }));
                            }}
                            className="bg-black border-zinc-850 rounded text-purple-600"
                          />
                          {mod}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* PRICING PLANS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Setup Price Placeholder</label>
                    <input
                      type="text"
                      value={formProduct.setupPrice || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, setupPrice: e.target.value }))}
                      placeholder="From £299"
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Monthly Price Placeholder</label>
                    <input
                      type="text"
                      value={formProduct.monthlyPrice || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, monthlyPrice: e.target.value }))}
                      placeholder="From £39/month"
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Integration Delivery Time</label>
                    <input
                      type="text"
                      value={formProduct.deliveryTime || '3 Days'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, deliveryTime: e.target.value }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white"
                    />
                  </div>
                </div>

                {/* TOGGLES STATUS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Automation Level</label>
                    <select
                      value={formProduct.automationLevel || 'Assisted Automation'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, automationLevel: e.target.value as any }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white"
                    >
                      <option value="Manual Setup">Manual Setup</option>
                      <option value="Assisted Automation">Assisted Automation</option>
                      <option value="Human Approved Automation">Human Approved Automation</option>
                      <option value="Semi-Automated">Semi-Automated</option>
                      <option value="Fully Automated Internal Only">Fully Automated Internal Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Require Human Approval</label>
                    <select
                      value={formProduct.humanApprovalRequired ? 'true' : 'false'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, humanApprovalRequired: e.target.value === 'true' }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white"
                    >
                      <option value="true">YES - Guarded</option>
                      <option value="false">NO - Direct</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Product Status</label>
                    <select
                      value={formProduct.productStatus || 'Idea'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, productStatus: e.target.value as any }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white uppercase font-mono"
                    >
                      <option value="Idea">Idea</option>
                      <option value="Draft">Draft</option>
                      <option value="Build Ready">Build Ready</option>
                      <option value="Client Preview Ready">Client Preview Ready</option>
                      <option value="Live Service">Live Service</option>
                      <option value="Paused">Paused</option>
                      <option value="Disabled">Disabled</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Billing Status Link</label>
                    <select
                      value={formProduct.billingStatus || 'Price Placeholder'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, billingStatus: e.target.value as any }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white font-mono"
                    >
                      <option value="Price Placeholder">Price Placeholder</option>
                      <option value="Invoice Only">Invoice Only</option>
                      <option value="Secure Checkout Ready">Secure Checkout Ready</option>
                      <option value="Subscription Ready">Subscription Ready</option>
                      <option value="Not Live Yet">Not Live Yet</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Public Landing Page status</label>
                    <select
                      value={formProduct.publicPageStatus || 'No Public Page'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, publicPageStatus: e.target.value as any }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white"
                    >
                      <option value="No Public Page">No Public Page</option>
                      <option value="Draft Landing Page">Draft Landing Page</option>
                      <option value="Ready for Review">Ready for Review</option>
                      <option value="Published">Published</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-500 uppercase block mb-1 font-bold">Demo Status</label>
                    <select
                      value={formProduct.demoStatus || 'None'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, demoStatus: e.target.value as any }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white"
                    >
                      <option value="None">None</option>
                      <option value="Draft">Draft</option>
                      <option value="Under Construction">Under Construction</option>
                      <option value="Active">Active</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* PRIVATE ADMIN ACCESS GATES AND COSTS */}
              <div className="space-y-4 p-4 bg-purple-950/10 border border-purple-500/20 rounded-2xl">
                <h4 className="font-mono text-[10px] uppercase font-bold text-[#e840f3] tracking-widest flex items-center gap-2">
                  <span>🔐 Admin Only Settings (Hidden from Clients)</span>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-purple-400 uppercase block mb-1 font-bold">Cost Estimate (£)</label>
                    <input
                      type="text"
                      value={formProduct.costEstimate || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, costEstimate: e.target.value }))}
                      placeholder="e.g. £15/mo server fees"
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-purple-400 uppercase block mb-1 font-bold">Margin Estimate (%)</label>
                    <input
                      type="text"
                      value={formProduct.marginEstimate || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, marginEstimate: e.target.value }))}
                      placeholder="e.g. 90%"
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-purple-400 uppercase block mb-1 font-bold">Difficulty Rank</label>
                    <select
                      value={formProduct.difficulty || 'Medium'}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, difficulty: e.target.value as any }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-1.5 text-white"
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                      <option value="Expert">Expert</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-purple-400 uppercase block mb-1 font-bold">Supplier/Provider Notes</label>
                    <input
                      type="text"
                      value={formProduct.supplierProviderNotes || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, supplierProviderNotes: e.target.value }))}
                      placeholder=" Shenzhen electronics/stable server providers..."
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-sans"
                    />
                  </div>
                  <div>
                    <label className="text-purple-400 uppercase block mb-1 font-bold">Risk Notes</label>
                    <input
                      type="text"
                      value={formProduct.riskNotes || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, riskNotes: e.target.value }))}
                      placeholder="Meta platform restrictions, DNS propagation delay..."
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-sans"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <label className="text-purple-400 uppercase block mb-1 font-bold">Internal Build Notes</label>
                    <textarea
                      rows={2}
                      value={formProduct.internalBuildNotes || ''}
                      onChange={(e) => setFormProduct(prev => ({ ...prev, internalBuildNotes: e.target.value }))}
                      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white placeholder-slate-600 font-sans"
                    />
                  </div>
                </div>
              </div>

              {/* SAVE BUTTONS */}
              <div className="flex justify-end gap-3 pt-4 border-t border-zinc-900 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsEditorOpen(false)}
                  className="px-4 py-2 bg-zinc-900 hover:bg-zinc-850 text-slate-350 rounded-lg cursor-pointer font-black uppercase font-mono text-[10px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black uppercase font-mono text-[10px] tracking-wider rounded-lg shadow-md cursor-pointer"
                >
                  Save Product Package
                </button>
              </div>

            </form>
          </motion.div>
        </div>
      )}

      {/* SALES PAGE GENERATOR PREVIEW MODAL */}
      {viewingLandingPageProduct && landingPageHtml && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-zinc-950 border border-cyan-400/35 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(0,255,255,0.15)] max-h-[92vh] flex flex-col text-left font-sans">
            
            {/* Modal Header */}
            <div className="p-5 bg-black border-b border-zinc-900 flex justify-between items-center bg-gradient-to-r from-cyan-950/20 via-black to-black shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#00ffff]" />
                <div>
                  <h4 className="font-extrabold text-white text-sm uppercase max-w-lg truncate leading-none">
                    Sales Landing Page Draft: {viewingLandingPageProduct.productName}
                  </h4>
                  <span className="text-[9px] font-mono text-amber-500 uppercase tracking-widest block mt-1 font-bold">
                    ⚠️ DRAFT OUTLINE ONLY — ADMIN APPROVAL REQUIRED BEFORE PUBLISHING
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setViewingLandingPageProduct(null);
                  setLandingPageHtml(null);
                }}
                className="text-slate-400 hover:text-white font-mono text-xs cursor-pointer uppercase font-black"
              >
                [ CLOSE PREVIEW ]
              </button>
            </div>

            {/* Simulated sales landing page */}
            <div className="p-8 overflow-y-auto space-y-8 bg-black text-slate-100 select-text">
              
              {/* META INFO HEADER PANEL */}
              <div className="p-4 bg-zinc-950 border border-zinc-900 rounded-2xl font-mono text-[10px] text-slate-500 space-y-1">
                <div><span className="text-[#00ffff] font-extrabold">Meta Title Tag:</span> {landingPageHtml.metaTitle}</div>
                <div><span className="text-[#e840f3] font-extrabold">Meta Description:</span> {landingPageHtml.metaDescription}</div>
                <div><span className="text-amber-500 font-extrabold">Draft state:</span> pending approval check.</div>
              </div>

              {/* HERO SECTION */}
              <div className="text-center py-6 border-b border-zinc-900 space-y-4">
                <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent text-xs font-mono font-black uppercase tracking-widest block">
                  DEPLOY HIGHEST FIDELITY AUTOMATION
                </span>
                <h1 className="text-2xl md:text-4xl font-sans font-black text-white uppercase tracking-tight max-w-3xl mx-auto leading-tight">
                  {landingPageHtml.heroHeadline}
                </h1>
                <p className="text-sm text-slate-300 max-w-2xl mx-auto leading-relaxed">
                  {landingPageHtml.shortPitch}
                </p>
                <div className="pt-2">
                  <button className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-indigo-600 font-mono text-[10.5px] font-black uppercase tracking-wider text-white rounded-xl shadow-lg cursor-pointer">
                    Get Started With {viewingLandingPageProduct.productName}
                  </button>
                </div>
              </div>

              {/* PROBLEM / SOLUTION GRID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div className="p-5 bg-rose-950/10 border border-rose-500/20 rounded-2xl space-y-2">
                  <div className="text-[10px] font-mono text-rose-400 uppercase font-bold tracking-widest">The Client Bottleneck</div>
                  <h4 className="text-md font-extrabold text-white uppercase leading-none">The Legacy Process</h4>
                  <p className="text-xs text-slate-400 leading-relaxed font-sans">{landingPageHtml.problem}</p>
                </div>
                <div className="p-5 bg-emerald-950/10 border border-emerald-500/20 rounded-2xl space-y-2">
                  <div className="text-[10px] font-mono text-emerald-400 uppercase font-bold tracking-widest">The Ultimate Resolve</div>
                  <h4 className="text-md font-extrabold text-white uppercase leading-none">The Nexus Strategy</h4>
                  <p className="text-xs text-slate-405 leading-relaxed font-sans">{landingPageHtml.solution}</p>
                </div>
              </div>

              {/* FEATURES & BENEFITS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-zinc-900">
                <div className="space-y-3">
                  <h4 className="font-mono text-[11px] uppercase text-[#00ffff] tracking-widest font-extrabold">What is Included in build</h4>
                  <ul className="space-y-2">
                    {landingPageHtml.features.map((feat: string, i: number) => (
                      <li key={i} className="flex gap-2 items-start text-xs text-slate-400">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3">
                  <h4 className="font-mono text-[11px] uppercase text-[#e840f3] tracking-widest font-extrabold">Measurable Outcomes</h4>
                  <ul className="space-y-2">
                    {landingPageHtml.benefits.map((ben: string, i: number) => (
                      <li key={i} className="flex gap-2 items-start text-xs text-slate-450">
                        <Check className="w-4 h-4 text-[#e840f3] shrink-0 mt-0.5" />
                        <span>{ben}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* PRICING SECTION */}
              <div className="p-6 bg-zinc-950 border border-zinc-900 rounded-3xl text-center space-y-3">
                <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest block">PRICING PACK PLAN</span>
                <div className="flex justify-center items-baseline gap-4">
                  <div>
                    <span className="text-[9px] text-slate-400 block uppercase font-mono">Setup Package Invoice</span>
                    <strong className="text-xl md:text-2xl text-white font-mono">{viewingLandingPageProduct.setupPrice}</strong>
                  </div>
                  <div className="h-6 w-[1px] bg-zinc-800"></div>
                  <div>
                    <span className="text-[9px] text-slate-400 block uppercase font-mono">Monthly Support Plan</span>
                    <strong className="text-xl md:text-2xl text-[#00ffff] font-mono">{viewingLandingPageProduct.monthlyPrice}</strong>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 italic font-mono">
                  * All pricing estimates are subject to custom briefing audits and integrations setup configurations complexity.
                </p>
              </div>

              {/* FREQUENTLY ASKED QUESTIONS */}
              <div className="space-y-4 pt-4 border-t border-zinc-900">
                <h4 className="font-mono text-[11px] uppercase text-white tracking-widest font-extrabold">Frequently Asked Questions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {landingPageHtml.faq.map((fq: any, i: number) => (
                    <div key={i} className="p-4 bg-zinc-950/50 border border-zinc-900 rounded-2xl space-y-1 shadow">
                      <strong className="text-xs text-[#00ffff] font-bold block">{fq.q}</strong>
                      <p className="text-xs text-slate-450 leading-relaxed font-sans mt-1">{fq.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* DRAFT CTA WARNING FOOTER */}
              <div className="p-4 bg-amber-950/20 border border-amber-500/25 rounded-2xl text-center text-xs text-amber-400 space-y-2">
                <p className="font-mono uppercase font-black tracking-wide">Publish Approval Pipeline Protection</p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => {
                      alert("Landing page publish request submitted to compliance officer.");
                      onAddLog('Product Factory', `Requested publication approval check for: ${viewingLandingPageProduct.productName} sales draft`, 'SUCCESS', 'Review queued on Master control board.');
                      setViewingLandingPageProduct(null);
                    }}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-mono font-black uppercase text-[9.5px] tracking-wide rounded-lg cursor-pointer"
                  >
                    Request Publish Approval
                  </button>
                  <button
                    onClick={() => {
                      setViewingLandingPageProduct(null);
                      setLandingPageHtml(null);
                    }}
                    className="px-4 py-2 bg-zinc-900 hover:bg-zinc-850 text-white font-mono text-[9.5px] uppercase rounded-lg cursor-pointer"
                  >
                    Close Preview
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* BILLING SIMULATION RESULT MODAL */}
      {billingSimResult && billingProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-zinc-950 border border-amber-500/35 rounded-2xl p-6 space-y-4 shadow-[0_0_40px_rgba(245,158,11,0.15)] text-left font-mono">
            
            <div className="flex items-center gap-2 border-b border-zinc-900 pb-3">
              <Zap className="w-5 h-5 text-amber-500" />
              <div>
                <h4 className="font-extrabold text-white text-xs uppercase">Billing Handshake Simulator</h4>
                <p className="text-[8px] text-slate-500">Node-07 Quantum billing check</p>
              </div>
            </div>

            <pre className="text-[10px] text-slate-350 bg-black p-4 rounded-xl border border-zinc-900 select-all whitespace-pre-wrap leading-relaxed">
              {billingSimResult}
            </pre>

            <div className="pt-2 flex justify-end gap-2 text-xs font-mono">
              <button
                onClick={() => {
                  alert("Invoice registered as draft. Shared in workspace logs.");
                  onAddLog('Billing Gateway System', `Succeeded saving custom dispatch draft invoice for ${billingProduct.productName}`, 'SUCCESS', `Invoice cached safely.`);
                  setBillingSimResult(null);
                  setBillingProduct(null);
                }}
                className="px-4 py-2 bg-amber-500 text-black font-black uppercase rounded cursor-pointer"
              >
                Log Invoice Draft
              </button>
              <button
                onClick={() => {
                  setBillingSimResult(null);
                  setBillingProduct(null);
                }}
                className="px-4 py-2 bg-zinc-900 hover:bg-zinc-850 text-white uppercase rounded cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
